import React, { useEffect, useState } from "react";
import { Award, Download, ExternalLink, CheckCircle, Lock, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/studentAuthContext";
import { navigateToPeerMate } from "../../utils/peermateHandoff";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

export default function Certification() {
  const { student: authStudent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paymentRef = queryParams.get("reference");
  const paymentStatus = queryParams.get("payment");

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const student = React.useMemo(() => {
    if (authStudent?.id) return authStudent;
    try {
      const raw = localStorage.getItem("student");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [authStudent]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!student?.id) return;
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URL}/api/ai/history/${student.id}`);
        const historyData = res.data?.data || [];
        
        // Enhance history with payment status
        const enhanced = await Promise.all(historyData.map(async (item) => {
            if (item.covered_topics === item.total_topics) {
                const statusRes = await axios.get(`${BASE_URL}/api/students/peermate/certificate-status/${student.id}/${encodeURIComponent(item.course_name)}`);
                return { ...item, isPaid: statusRes.data?.isPaid };
            }
            return item;
        }));
        
        setHistory(enhanced);
      } catch (err) {
        console.error("Failed to fetch PeerMate history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [student?.id]);

  useEffect(() => {
    const verifyPayment = async () => {
      if (paymentRef && paymentStatus === "peermate-cert") {
        setVerifyingPayment(true);
        try {
          await axios.post(`${BASE_URL}/api/students/peermate/verify-certificate`, { reference: paymentRef });
          // Refresh page to show download button
          navigate("/Certification", { replace: true });
          window.location.reload();
        } catch (err) {
          console.error("Payment verification failed:", err);
        } finally {
          setVerifyingPayment(false);
        }
      }
    };
    verifyPayment();
  }, [paymentRef, paymentStatus, navigate]);

  const printCertificate = (courseName, studentName, date) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Peer2Learn Certificate - ${courseName}</title>
          <style>
            body { font-family: 'Helvetica Neue', Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; background: #f8fafc; }
            .cert-card { width: 800px; padding: 48px; background: #ffffff; border: 10px solid #1e3a8a; box-shadow: 0 20px 40px rgba(0,0,0,0.1); text-align: center; border-radius: 16px; position: relative; }
            .badge { width: 70px; height: 70px; border-radius: 50%; background: #eff6ff; display: flex; align-items: center; justify-content: center; font-weight: bold; color: #2563eb; border: 3px solid #2563eb; margin: 0 auto 16px auto; font-size: 28px; }
            .cert-header { font-size: 13px; text-transform: uppercase; letter-spacing: 4px; color: #2563eb; font-weight: 800; margin-bottom: 12px; }
            .cert-title { font-size: 36px; font-weight: 900; color: #0f172a; margin: 0 0 16px 0; }
            .cert-recipient { font-size: 15px; color: #64748b; margin-bottom: 8px; }
            .student-name { font-size: 32px; font-weight: 800; color: #1e3a8a; border-bottom: 2px solid #e2e8f0; display: inline-block; padding-bottom: 6px; margin-bottom: 18px; }
            .cert-desc { font-size: 15px; line-height: 1.6; color: #334155; max-width: 600px; margin: 0 auto 28px auto; }
            .cert-footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 36px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
            @media print { body { background: none; } .cert-card { box-shadow: none; width: 100%; border-width: 8px; } }
          </style>
        </head>
        <body>
          <div class="cert-card">
            <div class="badge">★</div>
            <div class="cert-header">Certificate of Completion</div>
            <h1 class="cert-title">PEERMATE AI MASTERY</h1>
            <p class="cert-recipient">This acknowledges that</p>
            <div class="student-name">${studentName || 'Peer2Learn Scholar'}</div>
            <p class="cert-desc">has successfully completed the complete curriculum and verified all learning modules for <strong>${courseName}</strong> with demonstrated academic mastery on Peer2Learn.</p>
            <div class="cert-footer">
              <div style="text-align: left;"><p style="font-size: 12px; color: #94a3b8; margin: 0;">Date Issued</p><p style="font-size: 14px; font-weight: bold; color: #1e293b; margin: 4px 0 0 0;">${date || new Date().toLocaleDateString()}</p></div>
              <div style="text-align: right;"><p style="font-size: 12px; color: #94a3b8; margin: 0;">Verified by</p><p style="font-size: 14px; font-weight: bold; color: #1e293b; margin: 4px 0 0 0;">Peer2Learn Academic Board</p></div>
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadPdf = (item) => {
    const downloadUrl = `${BASE_URL}/api/students/peermate/certificate-pdf/${student?.id}/${encodeURIComponent(item.course_name)}`;
    window.open(downloadUrl, '_blank');
  };

  const handleHandleCertificate = async (item) => {
    if (item.isPaid) {
        handleDownloadPdf(item);
    } else {
        // Initialize Payment
        try {
            const res = await axios.post(`${BASE_URL}/api/students/peermate/initialize-certificate`, {
                studentId: student.id,
                courseName: item.course_name,
                email: student.email,
                name: student.name
            });
            if (res.data?.success && res.data.authorization_url) {
                window.location.href = res.data.authorization_url;
            }
        } catch (err) {
            console.error("Failed to initialize payment:", err);
            alert("Failed to start payment process. Please try again.");
        }
    }
  };

  if (loading || verifyingPayment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">
          {verifyingPayment ? "Confirming your certificate payment..." : "Loading your achievements..."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="p-3 bg-blue-600 rounded-2xl shadow-lg shadow-blue-200">
            <Award className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Certifications</h1>
            <p className="text-gray-500">Master new skills and earn recognized proof of your expertise.</p>
          </div>
        </div>

        {history.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-gray-100 shadow-sm text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Certifications Yet</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-8">
              Complete any PeerMate course syllabus to unlock your certificate. Start learning today to build your portfolio!
            </p>
            <button 
                type="button"
                onClick={() => navigateToPeerMate()}
                className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg"
            >
              Start Learning with PeerMate
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {history.map((item) => {
              const isCompleted = item.covered_topics === item.total_topics;
              const progress = Math.round((item.covered_topics / item.total_topics) * 100);

              return (
                <div key={item.course_name} className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition group">
                  <div className="flex justify-between items-start mb-6">
                    <div className={`p-3 rounded-xl ${isCompleted ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                      {isCompleted ? 'Completed' : `In Progress (${progress}%)`}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition truncate" title={item.course_name}>
                    {item.course_name}
                  </h3>
                  
                  <div className="w-full bg-gray-100 h-2 rounded-full mb-6 mt-4">
                    <div 
                        className={`h-full rounded-full transition-all duration-1000 ${isCompleted ? 'bg-emerald-500' : 'bg-blue-500'}`} 
                        style={{ width: `${progress}%` }}
                    />
                  </div>

                  {isCompleted ? (
                    <div className="space-y-2 w-full">
                      {item.isPaid ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadPdf(item)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold transition shadow-sm bg-emerald-600 text-white hover:bg-emerald-700 text-xs"
                          >
                            <Download className="w-4 h-4" /> Download PDF
                          </button>
                          <button
                            onClick={() => printCertificate(item.course_name, student?.name, new Date(item.last_activity || Date.now()).toLocaleDateString())}
                            className="p-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition text-xs"
                            title="Print / View Certificate"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleHandleCertificate(item)}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition shadow-sm bg-blue-600 text-white hover:bg-blue-700 text-xs"
                        >
                          <Lock className="w-4 h-4" /> Get Certificate (₦5,000 / $5)
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigateToPeerMate()}
                      className="w-full flex items-center justify-center gap-2 bg-gray-50 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-100 transition"
                    >
                      <ExternalLink className="w-5 h-5" /> Continue Learning
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 p-8 bg-blue-50 rounded-3xl border border-blue-100">
           <div className="flex gap-4">
              <div className="grow">
                <h4 className="text-lg font-bold text-blue-900 mb-2">Why get certified?</h4>
                <p className="text-blue-800/70 text-sm leading-relaxed">
                   PeerMate certificates are proof of your consistent learning and mastery. 
                   Each certificate is generated specifically for your unique custom learning path, 
                   making it a one-of-a-kind validation of your skills. Share them on LinkedIn or 
                   add them to your resume!
                </p>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}